import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    ThemeProvider,
    createTheme,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { extractList } from '../utils/response';
import { CREATE_TEAM } from '../navigations/path';
import { getTeams, deleteTeam } from '../store/slices/teamsSlice';
import SweetAlert from '../utils/sweetAlert';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../hooks/useAuth';
import Can from './permissions/Can';
// 🎨 Custom Theme
const revivePayTheme = createTheme({
    palette: {
        primary: {
            main: '#0a3d62', // Dark blue
        },
        success: {
            main: '#4caf50',
        },
        error: {
            main: '#d32f2f',
        },
    },
});

const TeamTableList = () => {
    const dispatch = useDispatch();
    const { hasPermission } = useAuth();

    // permission
    const canViewListTeam = hasPermission('team-list');
    const canCreateTeam = hasPermission('team-create');
    const canEditTeam = hasPermission('team-edit');
    const canViewTeam = hasPermission('team-view');
    const canDeleteTeam = hasPermission('team-delete');



    const navigate = useNavigate();
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [error, setError] = useState(null);
    const availableStatuses = ['all', 'active', 'inactive'];

    // Transform API data to match component structure
    const transformTeamsData = (apiTeams) => {
        return apiTeams.map(team => ({
            id: team.id,
            name: team.name,
            lead: team.members.find(member => member.role === 'lead')?.name || 'No Lead',
            leadId: team.members.find(member => member.role === 'lead')?.id,
            membersCount: team.members.length,
            members: team.members,
            created: team.created_at,
            status: team.status?.name || 'active',
        }));
    };



    // Fetch teams data
    useEffect(() => {
        const fetchTeams = async () => {
            if (!canViewListTeam) {
                toast.error('you donot have permission to views teams');
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const response = await dispatch(getTeams()).unwrap();
                const list = extractList(response);
                if (list.length > 0 || response) {
                    const transformedTeams = transformTeamsData(list);

                    setTeams(transformedTeams);
                } else {
                    toast.error('Invalid response format from server.');
                }
            } catch (error) {
                setError('Failed to load teams. Please try again.');
            } finally {
                setLoading(false);
            }
        };
        fetchTeams();
    }, [canViewListTeam]);

    // Filtering Logic
    const filteredTeams = teams.filter(team => {
        const lowerCaseSearch = searchTerm.toLowerCase();
        const statusMatch = filterStatus === 'all' || team.status === filterStatus;
        const searchMatch =
            team.name.toLowerCase().includes(lowerCaseSearch) ||
            team.lead.toLowerCase().includes(lowerCaseSearch);
        return statusMatch && searchMatch;
    });

    // Handlers
    const handleEdit = (teamId) => {
        if (!canEditTeam) return;
        navigate(`${CREATE_TEAM}?mode=edit&id=${teamId}`);
    };

    const handleView = (teamId) => {
        if (!canViewTeam) return;
        navigate(`${CREATE_TEAM}?mode=view&id=${teamId}`);
    };

    const handleDelete = async (teamId) => {
        if (!canDeleteTeam) return;
        try {
            // Show SweetAlert confirmation
            const result = await SweetAlert.deleteConfirm('this Team');

            // If user cancels, return early
            if (!result.isConfirmed) {
                return;
            }

            await dispatch(deleteTeam(teamId)).unwrap();
            await SweetAlert.success({
                text: 'team deleted successfully.'
            });
            setTeams(teams.filter(team => team.id !== teamId));
        } catch (error) {
            console.error('Delete action error:', error);
            await SweetAlert.error({
                text: 'Failed to delete team record'
            });
        }
    };


    const handleCreateTeam = () => {
        if (!canCreateTeam) return;
        navigate(CREATE_TEAM);
    };

    const getStatusChip = (status) => {
        const color = status === 'active' ? 'success' : 'error';
        const label = status === 'active' ? 'Active' : 'Inactive';
        return (
            <Chip
                label={label}
                color={color}
                size="small"
                sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
            />
        );
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    // Loading State
    if (loading) {
        return (
            <ThemeProvider theme={revivePayTheme}>
                <Box sx={{ flexGrow: 1, p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <div style={{ width: 50, height: 50, border: '4px solid #f3f3f3', borderTop: '4px solid #0a3d62', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                        <Typography variant="h6" color="textSecondary">
                            Loading teams...
                        </Typography>
                    </div>
                </Box>
            </ThemeProvider>
        );
    }

    // Error State
    if (error) {
        return (
            <ThemeProvider theme={revivePayTheme}>
                <Box sx={{ flexGrow: 1, p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <Paper elevation={3} sx={{ p: 4, textAlign: 'center', maxWidth: 500 }}>
                        <Typography variant="h6" color="error" gutterBottom>
                            {error}
                        </Typography>
                        <Button variant="contained" color="primary" onClick={() => window.location.reload()}>
                            Retry
                        </Button>
                    </Paper>
                </Box>
            </ThemeProvider>
        );
    }

    // Empty State
    if (!loading && teams.length === 0) {
        return (
            <ThemeProvider theme={revivePayTheme}>
                <Box sx={{ flexGrow: 1, p: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                            Team Management
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={handleCreateTeam}
                            sx={{ borderRadius: 2, px: { xs: 2, sm: 3 }, py: { xs: 1, sm: 1.5 } }}
                        >
                            Create Team
                        </Button>
                    </Box>
                    <Paper elevation={3} sx={{ p: 4, textAlign: 'center', maxWidth: 600, margin: '0 auto' }}>
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                            No teams found
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                            Get started by creating your first team.
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={handleCreateTeam}
                            size="large"
                        >
                            Create Your First Team
                        </Button>
                    </Paper>
                </Box>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={revivePayTheme}>
            <Box sx={{ flexGrow: 1, p: 4, bgcolor: '#fff' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                        Team Management
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Can permission='team-create'>
                            <Typography variant="body2" color="textSecondary">
                                Total Teams: {teams.length}
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleCreateTeam}
                                sx={{ borderRadius: 2, px: { xs: 2, sm: 3 }, py: { xs: 1, sm: 1.5 } }}
                            >
                                Create Team
                            </Button>
                        </Can>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap', alignItems: 'center', p: 2, bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
                    <TextField
                        label="Search Name or Lead"
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ minWidth: 250 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel id="status-filter-label">Filter by Status</InputLabel>
                        <Select
                            labelId="status-filter-label"
                            value={filterStatus}
                            label="Filter by Status"
                            onChange={(e) => setFilterStatus(e.target.value)}
                            sx={{ borderRadius: 2 }}
                        >
                            {availableStatuses.map(status => (
                                <MenuItem key={status} value={status}>
                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Typography variant="body2" color="textSecondary" sx={{ ml: 'auto' }}>
                        Showing {filteredTeams.length} of {teams.length} teams
                    </Typography>
                </Box>
                <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table stickyHeader aria-label="team list table">
                            <TableHead>
                                <TableRow sx={{ bgcolor: revivePayTheme.palette.primary.main, '& th': { color: 'black', fontWeight: 'bold', border: 0 } }}>
                                    {/* <TableCell>ID</TableCell> */}
                                    <TableCell>Team Name</TableCell>
                                    <TableCell>Team Lead</TableCell>
                                    <TableCell>Members</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredTeams.map((team) => (
                                    <TableRow key={team.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 }, transition: 'background-color 0.2s ease' }}>
                                        {/* <TableCell component="th" scope="row" sx={{ fontWeight: 'medium' }}>
                                            {team.id.slice(-8)}
                                        </TableCell> */}
                                        <TableCell sx={{ fontWeight: 'medium' }}>{team.name}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                                                    {team.lead}
                                                </Typography>
                                                {team.leadId && <Chip label="Lead" size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem' }} />}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={`${team.membersCount} member${team.membersCount !== 1 ? 's' : ''}`} size="small" color="default" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="textSecondary">
                                                {formatDate(team.created)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{getStatusChip(team.status)}</TableCell>
                                        <TableCell align="center">
                                            <Can permission='team-view' >
                                                <IconButton onClick={() => handleView(team.id)} aria-label="view team" color="primary" size="small" sx={{ mr: 0.5 }}>
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Can>
                                            <Can permission='team-edit'>
                                                <IconButton onClick={() => handleEdit(team.id)} aria-label="edit team" color="success" size="small" sx={{ mr: 0.5 }}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Can>
                                            <Can permission='team-delete'>
                                                <IconButton onClick={() => handleDelete(team.id)} aria-label="delete team" color="error" size="small">
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Can>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
                {!loading && filteredTeams.length === 0 && teams.length > 0 && (
                    <Box sx={{ mt: 4, textAlign: 'center', p: 4 }}>
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                            No teams match your search
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                            Try adjusting your search terms or filters.
                        </Typography>
                        <Button variant="outlined" color="primary" onClick={() => { setSearchTerm(''); setFilterStatus('all'); }}>
                            Clear Filters
                        </Button>
                    </Box>
                )}
            </Box>
            <style jsx>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </ThemeProvider>
    );
};

export default TeamTableList;